import React, { useId } from 'react';
import { motion } from 'framer-motion';

// Helper to convert hex to RGB
const hexToRgb = (hex) => {
  if (!hex) return '255, 255, 255';
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) || 0;
  const g = parseInt(hex.substring(2, 4), 16) || 0;
  const b = parseInt(hex.substring(4, 6), 16) || 0;
  return `${r}, ${g}, ${b}`;
};

// Lighten a hex color
const lightenColor = (hex, percent) => {
  if (!hex) return '#FFFFFF';
  hex = hex.replace('#', '');
  const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + Math.round(255 * percent));
  const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + Math.round(255 * percent));
  const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + Math.round(255 * percent));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Darken a hex color
const darkenColor = (hex, percent) => {
  if (!hex) return '#000000';
  hex = hex.replace('#', '');
  const r = Math.max(0, parseInt(hex.substring(0, 2), 16) - Math.round(255 * percent));
  const g = Math.max(0, parseInt(hex.substring(2, 4), 16) - Math.round(255 * percent));
  const b = Math.max(0, parseInt(hex.substring(4, 6), 16) - Math.round(255 * percent));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Frame style definitions
export const FRAME_STYLES = {
  // === CLASSIC – timeless, minimal ===
  clean: {
    id: 'clean',
    name: 'Clean',
    category: 'classic',
    description: 'Minimal modern look',
  },
  shadow: {
    id: 'shadow',
    name: 'Elevated',
    category: 'classic',
    description: 'Floating card with deep shadow',
  },
  bordered: {
    id: 'bordered',
    name: 'Simple Border',
    category: 'classic',
    description: 'Clean single border',
  },
  rounded: {
    id: 'rounded',
    name: 'Pill Shape',
    category: 'classic',
    description: 'Extra rounded corners',
  },
  letterbox: {
    id: 'letterbox',
    name: 'Letterbox',
    category: 'classic',
    description: 'Cinematic wide bars',
  },
  notebook: {
    id: 'notebook',
    name: 'Notebook',
    category: 'classic',
    description: 'Paper margin and soft shadow',
  },

  // === RETRO – vintage, nostalgic ===
  pixel: {
    id: 'pixel',
    name: 'Pixel Art',
    category: 'retro',
    description: '8-bit game style',
  },
  arcade: {
    id: 'arcade',
    name: 'Arcade Cabinet',
    category: 'retro',
    description: 'Classic arcade machine',
  },
  crt: {
    id: 'crt',
    name: 'CRT Monitor',
    category: 'retro',
    description: 'Retro TV with scanlines',
  },
  vhs: {
    id: 'vhs',
    name: 'VHS Tape',
    category: 'retro',
    description: '80s video aesthetic',
  },
  polaroid: {
    id: 'polaroid',
    name: 'Polaroid',
    category: 'retro',
    description: 'Instant photo frame',
  },
  filmstrip: {
    id: 'filmstrip',
    name: 'Film Strip',
    category: 'retro',
    description: 'Classic film perforations',
  },
  comic: {
    id: 'comic',
    name: 'Comic Pop',
    category: 'retro',
    description: 'Bold comic-book outline',
  },

  // === MODERN – sleek, tech ===
  neon: {
    id: 'neon',
    name: 'Neon Glow',
    category: 'modern',
    description: 'Vibrant glowing border',
  },
  gradient: {
    id: 'gradient',
    name: 'Gradient Border',
    category: 'modern',
    description: 'Rainbow edge effect',
  },
  glass: {
    id: 'glass',
    name: 'Glassmorphism',
    category: 'modern',
    description: 'Frosted glass effect',
  },
  minimal: {
    id: 'minimal',
    name: 'Hairline',
    category: 'modern',
    description: 'Ultra-thin border',
  },
  outline: {
    id: 'outline',
    name: 'Outline',
    category: 'modern',
    description: 'Hollow double stroke',
  },
  softglow: {
    id: 'softglow',
    name: 'Soft Glow',
    category: 'modern',
    description: 'Diffused soft halo',
  },

  // === PLAYFUL – fun, expressive ===
  double: {
    id: 'double',
    name: 'Double Frame',
    category: 'playful',
    description: 'Inner and outer borders',
  },
  triple: {
    id: 'triple',
    name: 'Triple Layer',
    category: 'playful',
    description: 'Three nested frames',
  },
  dashed: {
    id: 'dashed',
    name: 'Dashed',
    category: 'playful',
    description: 'Fun dashed border',
  },
  dotted: {
    id: 'dotted',
    name: 'Dotted',
    category: 'playful',
    description: 'Playful dot pattern',
  },
  sticker: {
    id: 'sticker',
    name: 'Sticker',
    category: 'playful',
    description: 'Bold offset sticker shadow',
  },
  wavy: {
    id: 'wavy',
    name: 'Wavy',
    category: 'playful',
    description: 'Soft wavy edge',
  },

  // === PREMIUM – bold, luxe ===
  gold: {
    id: 'gold',
    name: 'Gold Foil',
    category: 'premium',
    description: 'Metallic gold border',
  },
  metallic: {
    id: 'metallic',
    name: 'Metallic',
    category: 'premium',
    description: 'Silver steel edge',
  },
  spotlight: {
    id: 'spotlight',
    name: 'Spotlight',
    category: 'premium',
    description: 'Strong single-edge highlight',
  },
};

// Categories for the picker (order = tab order)
export const FRAME_CATEGORIES = [
  { id: 'classic', name: 'Classic', description: 'Timeless & minimal' },
  { id: 'retro', name: 'Retro', description: 'Vintage & nostalgic' },
  { id: 'modern', name: 'Modern', description: 'Sleek & tech' },
  { id: 'playful', name: 'Playful', description: 'Fun & expressive' },
  { id: 'premium', name: 'Premium', description: 'Bold & luxe' },
];

// Optional overlay effects – can be combined with any frame style
export const CARD_OVERLAY_EFFECTS = {
  none: { id: 'none', name: 'None', description: 'No overlay' },
  grain: { id: 'grain', name: 'Film Grain', description: 'Subtle film grain texture' },
  confetti: { id: 'confetti', name: 'Confetti', description: 'Animated falling shapes' },
  snowfall: { id: 'snowfall', name: 'Snowfall', description: 'Gentle falling snowflakes' },
  sparkles: { id: 'sparkles', name: 'Sparkles', description: 'Twinkling stars' },
  bubbles: { id: 'bubbles', name: 'Bubbles', description: 'Floating bubbles' },
  hearts: { id: 'hearts', name: 'Hearts', description: 'Falling hearts' },
  leaves: { id: 'leaves', name: 'Leaves', description: 'Drifting autumn leaves' },
  stars: { id: 'stars', name: 'Stars', description: 'Rising gold stars' },
  fireworks: { id: 'fireworks', name: 'Fireworks', description: 'Celebratory bursts' },
  embers: { id: 'embers', name: 'Embers', description: 'Warm floating particles' },
};

// Optional background patterns – rendered on the card surface behind content
export const CARD_BACKGROUND_PATTERNS = {
  none: { id: 'none', name: 'None', description: 'No pattern' },
  dots: { id: 'dots', name: 'Dots', description: 'Subtle dot grid' },
  diagonalLines: { id: 'diagonalLines', name: 'Diagonal Lines', description: 'Thin angled stripes' },
  grid: { id: 'grid', name: 'Grid', description: 'Graph-paper grid' },
  topographic: { id: 'topographic', name: 'Topographic', description: 'Organic contour lines' },
};

// Curated subset of frames shown in the inline picker (all 31 still render for backward compat)
export const CURATED_FRAMES = [
  'clean', 'bordered',
  'pixel', 'crt',
  'neon', 'gradient', 'glass',
  'sticker',
];

// Static overlay elements
const SCANLINES_OVERLAY = (
  <div
    key="scanlines"
    className="absolute inset-0 pointer-events-none opacity-[0.05]"
    style={{
      background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.2) 2px, rgba(0,0,0,0.2) 3px)',
    }}
  />
);

const VHS_OVERLAY = (
  <div key="vhs" className="absolute inset-0 pointer-events-none overflow-hidden">
    {/* Scanline base */}
    <div
      className="absolute inset-0 opacity-[0.03]"
      style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.15) 2px, rgba(0,0,0,0.15) 3px)',
      }}
    />
    {/* Animated tracking band */}
    <div
      className="absolute inset-x-0 opacity-[0.12]"
      style={{
        height: '12%',
        background: `linear-gradient(0deg,
          transparent 0%,
          rgba(233, 69, 96, 0.3) 30%,
          rgba(0, 255, 255, 0.25) 50%,
          rgba(233, 69, 96, 0.3) 70%,
          transparent 100%
        )`,
        animation: 'vhs-track 4s ease-in-out infinite',
      }}
    />
    <style>{`@keyframes vhs-track { 0%,100% { top: -12%; } 50% { top: 100%; } }`}</style>
  </div>
);

// Color-aware overlay functions
const getGrainOverlay = (filterId) => (
  <div
    key="grain"
    className="absolute inset-0 pointer-events-none opacity-[0.28]"
    style={{ mixBlendMode: 'multiply' }}
  >
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
      <filter id={filterId}>
        <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
      </filter>
      <rect width="100%" height="100%" filter={`url(#${filterId})`} />
    </svg>
  </div>
);

const getConfettiOverlay = (primaryColor) => (
  <div
    key="confetti"
    className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.45]"
  >
    <style>{`
      @keyframes cf-fall { 0% { transform: translateY(-30px) rotate(0deg); } 100% { transform: translateY(700px) rotate(720deg); } }
    `}</style>
    {[...Array(14)].map((_, i) => (
      <div
        key={i}
        className="absolute"
        style={{
          width: `${6 + (i % 3) * 4}px`,
          height: `${6 + (i % 3) * 4}px`,
          backgroundColor: primaryColor,
          borderRadius: i % 3 === 0 ? '50%' : i % 3 === 1 ? '2px' : '0',
          left: `${(i * 7.1) % 96 + 2}%`,
          top: '-30px',
          opacity: 0.6 + (i % 3) * 0.15,
          animation: `cf-fall ${3.5 + (i % 4) * 0.8}s linear ${i * 0.25}s infinite`,
        }}
      />
    ))}
  </div>
);

const getSnowfallOverlay = () => (
  <div
    key="snowfall"
    className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.55]"
  >
    <style>{`
      @keyframes snow-fall { 0% { transform: translateY(-10px) translateX(0); } 50% { transform: translateY(350px) translateX(15px); } 100% { transform: translateY(700px) translateX(-5px); } }
    `}</style>
    {[...Array(18)].map((_, i) => (
      <div
        key={i}
        className="absolute rounded-full"
        style={{
          width: `${3 + (i % 4) * 2}px`,
          height: `${3 + (i % 4) * 2}px`,
          backgroundColor: '#FFFFFF',
          left: `${(i * 5.5) % 96 + 2}%`,
          top: '-10px',
          opacity: 0.5 + (i % 3) * 0.2,
          animation: `snow-fall ${5 + (i % 5) * 1.2}s linear ${i * 0.35}s infinite`,
          boxShadow: '0 0 3px rgba(255,255,255,0.4)',
        }}
      />
    ))}
  </div>
);

const SPARKLE_POSITIONS = [
  { x: 8, y: 12 }, { x: 72, y: 5 }, { x: 35, y: 68 }, { x: 88, y: 42 },
  { x: 15, y: 85 }, { x: 55, y: 22 }, { x: 42, y: 48 }, { x: 78, y: 78 },
  { x: 25, y: 35 }, { x: 62, y: 90 }, { x: 92, y: 15 }, { x: 5, y: 55 },
];

const getSparklesOverlay = (primaryColor) => (
  <div
    key="sparkles"
    className="absolute inset-0 pointer-events-none overflow-hidden"
  >
    <style>{`
      @keyframes sparkle-twinkle {
        0%, 100% { opacity: 0; transform: scale(0); }
        50% { opacity: 0.6; transform: scale(1); }
      }
    `}</style>
    {SPARKLE_POSITIONS.map((pos, i) => {
      const size = 6 + (i % 3) * 3;
      const color = i % 4 === 0 ? primaryColor : '#FFFFFF';
      return (
        <svg
          key={i}
          className="absolute"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          style={{
            left: `${pos.x}%`,
            top: `${pos.y}%`,
            opacity: 0,
            animation: `sparkle-twinkle ${3 + (i % 5) * 0.7}s ease-in-out ${i * 0.55}s infinite`,
            filter: `drop-shadow(0 0 2px ${color})`,
          }}
        >
          <path
            d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z"
            fill={color}
          />
        </svg>
      );
    })}
  </div>
);

const getBubblesOverlay = () => (
  <div
    key="bubbles"
    className="absolute inset-0 pointer-events-none overflow-hidden"
  >
    <style>{`
      @keyframes bubble-rise {
        0% { transform: translateY(0) scale(0.5); opacity: 0; }
        10% { opacity: 0.6; transform: translateY(-60px) scale(0.75); }
        50% { opacity: 0.5; }
        100% { transform: translateY(-700px) scale(1); opacity: 0; }
      }
      @keyframes bubble-sway {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(8px); }
        75% { transform: translateX(-8px); }
      }
    `}</style>
    {[...Array(10)].map((_, i) => {
      const size = 10 + (i % 4) * 7;
      return (
        <div
          key={i}
          className="absolute"
          style={{
            left: `${(i * 9.7 + 5) % 88}%`,
            bottom: `-${size}px`,
            animation: `bubble-rise ${7 + (i % 4) * 2}s linear ${i * 0.8}s infinite`,
          }}
        >
          <div
            className="rounded-full"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.12) 40%, rgba(255,255,255,0.04) 70%, transparent 100%)`,
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: `inset -${size * 0.12}px -${size * 0.08}px ${size * 0.15}px rgba(255,255,255,0.08), inset ${size * 0.08}px ${size * 0.08}px ${size * 0.12}px rgba(255,255,255,0.15)`,
              animation: `bubble-sway ${3 + (i % 3) * 0.8}s ease-in-out ${i * 0.3}s infinite`,
            }}
          />
        </div>
      );
    })}
  </div>
);

const getHeartsOverlay = (primaryColor) => (
  <div
    key="hearts"
    className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.4]"
  >
    <style>{`
      @keyframes heart-fall { 0% { transform: translateY(-20px) rotate(0deg) scale(0.8); } 50% { transform: translateY(350px) rotate(15deg) scale(1); } 100% { transform: translateY(700px) rotate(-10deg) scale(0.6); } }
    `}</style>
    {[...Array(12)].map((_, i) => (
      <div
        key={i}
        className="absolute"
        style={{
          fontSize: `${10 + (i % 3) * 5}px`,
          color: primaryColor,
          left: `${(i * 8.2) % 94 + 3}%`,
          top: '-20px',
          animation: `heart-fall ${4.5 + (i % 4) * 1}s linear ${i * 0.4}s infinite`,
        }}
      >
        &#9829;
      </div>
    ))}
  </div>
);

const getLeavesOverlay = () => (
  <div
    key="leaves"
    className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.4]"
  >
    <style>{`
      @keyframes leaf-fall { 0% { transform: translateY(-20px) rotate(0deg) translateX(0); } 33% { transform: translateY(230px) rotate(90deg) translateX(30px); } 66% { transform: translateY(460px) rotate(200deg) translateX(-15px); } 100% { transform: translateY(700px) rotate(360deg) translateX(10px); } }
    `}</style>
    {[...Array(10)].map((_, i) => {
      const colors = ['#D97706', '#B45309', '#92400E', '#DC2626', '#F59E0B'];
      return (
        <div
          key={i}
          className="absolute"
          style={{
            fontSize: `${10 + (i % 3) * 4}px`,
            color: colors[i % colors.length],
            left: `${(i * 9.8) % 92 + 4}%`,
            top: '-20px',
            animation: `leaf-fall ${5.5 + (i % 4) * 1.4}s linear ${i * 0.55}s infinite`,
          }}
        >
          &#127809;
        </div>
      );
    })}
  </div>
);

const getStarsOverlay = (primaryColor) => (
  <div
    key="stars"
    className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.5]"
  >
    <style>{`
      @keyframes star-rise {
        0% { transform: translateY(700px) scale(0.4) rotate(0deg); opacity: 0; }
        15% { opacity: 0.8; }
        85% { opacity: 0.6; }
        100% { transform: translateY(-30px) scale(1) rotate(120deg); opacity: 0; }
      }
    `}</style>
    {[...Array(10)].map((_, i) => {
      const size = 10 + (i % 3) * 5;
      const color = i % 3 === 0 ? primaryColor : '#FFD700';
      return (
        <svg
          key={i}
          className="absolute"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          style={{
            left: `${(i * 9.5 + 3) % 90}%`,
            bottom: '-20px',
            animation: `star-rise ${6 + (i % 4) * 1.5}s linear ${i * 0.7}s infinite`,
            filter: `drop-shadow(0 0 3px ${color})`,
          }}
        >
          <path
            d="M12 2 L14.9 8.6 L22 9.3 L16.8 14 L18.2 21 L12 17.5 L5.8 21 L7.2 14 L2 9.3 L9.1 8.6 Z"
            fill={color}
          />
        </svg>
      );
    })}
  </div>
);

const FIREWORK_LAUNCHES = [
  { x: 20, y: 20 }, { x: 70, y: 15 }, { x: 45, y: 30 },
  { x: 85, y: 25 }, { x: 15, y: 35 }, { x: 60, y: 18 },
];

const getFireworksOverlay = (primaryColor) => {
  const colors = [primaryColor, '#FFD700', '#FF6B6B', '#4ECDC4', '#A78BFA', '#F97316'];
  const sparkAngles = [0, 45, 90, 135, 180, 225, 270, 315];

  // Build all keyframes: one launch trail + 8 spark bursts per firework
  const allKeyframes = FIREWORK_LAUNCHES.map((pos, i) => {
    // Trail rises from bottom to target y, then vanishes
    const risePercent = 35; // first 35% of cycle is the rise
    const trail = `@keyframes fw-trail-${i} {
      0% { bottom: -5%; left: ${pos.x}%; opacity: 0; }
      5% { opacity: 1; }
      ${risePercent}% { bottom: ${100 - pos.y}%; left: ${pos.x}%; opacity: 1; }
      ${risePercent + 1}% { opacity: 0; }
      100% { opacity: 0; }
    }`;
    // Burst flash at the target
    const burst = `@keyframes fw-pop-${i} {
      0%, ${risePercent}% { transform: scale(0); opacity: 0; }
      ${risePercent + 2}% { transform: scale(1); opacity: 0.8; }
      ${risePercent + 20}% { transform: scale(2); opacity: 0; }
      100% { opacity: 0; }
    }`;
    // Sparks radiate out from the burst point
    const sparks = sparkAngles.map((angle, j) => {
      const dist = 18 + (j % 3) * 10;
      const tx = Math.round(Math.cos(angle * Math.PI / 180) * dist);
      const ty = Math.round(Math.sin(angle * Math.PI / 180) * dist);
      return `@keyframes fw-s-${i}-${j} {
        0%, ${risePercent}% { transform: translate(0,0); opacity: 0; }
        ${risePercent + 2}% { transform: translate(0,0); opacity: 0.9; }
        ${risePercent + 22}% { transform: translate(${tx}px,${ty}px); opacity: 0; }
        100% { opacity: 0; }
      }`;
    }).join('\n');
    return `${trail}\n${burst}\n${sparks}`;
  }).join('\n');

  return (
    <div
      key="fireworks"
      className="absolute inset-0 pointer-events-none overflow-hidden"
    >
      <style>{allKeyframes}</style>
      {FIREWORK_LAUNCHES.map((pos, i) => {
        const color = colors[i % colors.length];
        const cycleDuration = 4 + (i % 3) * 0.8;
        const delay = i * 1.1;
        return (
          <div key={i}>
            {/* Rising trail */}
            <div
              className="absolute rounded-full"
              style={{
                width: '3px',
                height: '3px',
                backgroundColor: color,
                boxShadow: `0 0 6px 2px ${color}`,
                animation: `fw-trail-${i} ${cycleDuration}s ease-in ${delay}s infinite`,
                opacity: 0,
              }}
            />
            {/* Burst + sparks at target position */}
            <div
              className="absolute"
              style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
            >
              <div
                className="absolute rounded-full"
                style={{
                  width: '5px',
                  height: '5px',
                  backgroundColor: color,
                  boxShadow: `0 0 10px 4px ${color}`,
                  transform: 'translate(-50%, -50%)',
                  opacity: 0,
                  animation: `fw-pop-${i} ${cycleDuration}s ease-out ${delay}s infinite`,
                }}
              />
              {sparkAngles.map((_, j) => (
                <div
                  key={j}
                  className="absolute rounded-full"
                  style={{
                    width: '2px',
                    height: '2px',
                    backgroundColor: color,
                    boxShadow: `0 0 3px ${color}`,
                    opacity: 0,
                    animation: `fw-s-${i}-${j} ${cycleDuration}s ease-out ${delay}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getPetalsOverlay = () => (
  <div
    key="petals"
    className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.45]"
  >
    <style>{`
      @keyframes petal-drift {
        0% { transform: translateY(-15px) rotate(0deg) translateX(0); }
        25% { transform: translateY(175px) rotate(45deg) translateX(20px); }
        50% { transform: translateY(350px) rotate(120deg) translateX(-10px); }
        75% { transform: translateY(525px) rotate(200deg) translateX(15px); }
        100% { transform: translateY(700px) rotate(300deg) translateX(-5px); }
      }
    `}</style>
    {[...Array(12)].map((_, i) => {
      const colors = ['#FFB7C5', '#FFC1CC', '#FF9EAF', '#FFDDE4', '#FFA5B4'];
      return (
        <div
          key={i}
          className="absolute"
          style={{
            width: `${6 + (i % 3) * 3}px`,
            height: `${8 + (i % 3) * 4}px`,
            backgroundColor: colors[i % colors.length],
            borderRadius: '50% 0 50% 0',
            left: `${(i * 8.3 + 2) % 94}%`,
            top: '-15px',
            animation: `petal-drift ${6 + (i % 5) * 1.2}s linear ${i * 0.45}s infinite`,
          }}
        />
      );
    })}
  </div>
);

const getEmbersOverlay = () => (
  <div
    key="embers"
    className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.5]"
  >
    <style>{`
      @keyframes ember-rise {
        0% { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
        10% { opacity: 0.9; }
        50% { transform: translateY(-300px) translateX(12px) scale(0.8); opacity: 0.7; }
        100% { transform: translateY(-650px) translateX(-8px) scale(0.3); opacity: 0; }
      }
    `}</style>
    {[...Array(14)].map((_, i) => {
      const colors = ['#FF6B35', '#FFA62B', '#FFD166', '#FF4500', '#FF8C42'];
      const size = 2 + (i % 3) * 2;
      return (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: colors[i % colors.length],
            boxShadow: `0 0 ${size + 2}px ${colors[i % colors.length]}`,
            left: `${(i * 7.1 + 3) % 92}%`,
            bottom: '-5px',
            animation: `ember-rise ${4 + (i % 5) * 1.3}s ease-out ${i * 0.4}s infinite`,
          }}
        />
      );
    })}
  </div>
);

const CardFrame = ({
  children,
  frameStyle = 'clean',
  colors = {},
  className = '',
  animate = true,
  fullWidth = false,
  overlayEffect = null, // optional overlay on any frame
  backgroundPattern = null, // optional background pattern on card surface
  exitWithParent = false, // when true, no exit on frame – parent (e.g. StartCard) runs exit so border fades with card
}) => {
  const instanceId = useId();
  const primary = colors?.primaryAction || '#7C3AED';
  const secondary = colors?.secondaryAction || '#F97316';
  const surface = colors?.surface || '#FFFFFF';
  const _background = colors?.background || '#000000';
  const text = colors?.text || '#000000';

  const Wrapper = animate ? motion.div : 'div';
  const wrapperProps = animate ? {
    initial: { opacity: 0, y: 30, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    ...(exitWithParent ? {} : { exit: { opacity: 0, scale: 0.95, y: 30 } }),
    transition: { duration: 0.4, ease: [0.4, 0.0, 0.2, 1] },
  } : {};

  // Effective overlay: explicit choice, or default for CRT/VHS frames
  const effectiveOverlay = overlayEffect === 'none' || overlayEffect === null || overlayEffect === undefined
    ? (frameStyle === 'crt' ? 'scanlines' : frameStyle === 'vhs' ? 'vhs' : null)
    : overlayEffect;

  const getOverlayContent = (effect) => {
    switch (effect) {
      case 'scanlines': return SCANLINES_OVERLAY;
      case 'vhs': return VHS_OVERLAY;
      case 'grain': return getGrainOverlay(`grain-${instanceId}`);
      case 'confetti': return getConfettiOverlay(primary);
      case 'snowfall': return getSnowfallOverlay();
      case 'sparkles': return getSparklesOverlay(primary);
      case 'bubbles': return getBubblesOverlay();
      case 'hearts': return getHeartsOverlay(primary);
      case 'leaves': return getLeavesOverlay();
      case 'stars': return getStarsOverlay(primary);
      case 'fireworks': return getFireworksOverlay(primary);
      case 'petals': return getPetalsOverlay();
      case 'embers': return getEmbersOverlay();
      default: return null;
    }
  };
  const overlayContent = getOverlayContent(effectiveOverlay);

  // Background pattern content
  const getPatternContent = (pattern) => {
    if (!pattern || pattern === 'none') return null;
    const rgb = hexToRgb(text);
    const patternStyles = {
      dots: {
        backgroundImage: `radial-gradient(circle, rgba(${rgb}, 0.04) 1px, transparent 1px)`,
        backgroundSize: '16px 16px',
      },
      diagonalLines: {
        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(${rgb}, 0.03) 10px, rgba(${rgb}, 0.03) 11px)`,
      },
      grid: {
        backgroundImage: `
          repeating-linear-gradient(0deg, rgba(${rgb}, 0.03) 0px, rgba(${rgb}, 0.03) 1px, transparent 1px, transparent 40px),
          repeating-linear-gradient(90deg, rgba(${rgb}, 0.03) 0px, rgba(${rgb}, 0.03) 1px, transparent 1px, transparent 40px)
        `,
      },
      topographic: {
        backgroundImage: `
          radial-gradient(ellipse at 30% 40%, transparent 60%, rgba(${rgb}, 0.04) 61%, transparent 62%),
          radial-gradient(ellipse at 70% 30%, transparent 50%, rgba(${rgb}, 0.03) 51%, transparent 52%),
          radial-gradient(ellipse at 50% 70%, transparent 45%, rgba(${rgb}, 0.05) 46%, transparent 47%),
          radial-gradient(ellipse at 20% 80%, transparent 55%, rgba(${rgb}, 0.03) 56%, transparent 57%)
        `,
        backgroundSize: '200px 200px, 180px 180px, 160px 160px, 220px 220px',
      },
    };
    return (
      <div
        key="bg-pattern"
        className="absolute inset-0 pointer-events-none"
        style={patternStyles[pattern] || {}}
      />
    );
  };
  const patternContent = getPatternContent(backgroundPattern);

  // Render different frame structures based on style
  const renderFrame = () => {
    switch (frameStyle) {
      // === CLEAN - Simple shadow ===
      case 'clean':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === SHADOW - Deep floating shadow ===
      case 'shadow':
        return (
          <div 
            className="relative rounded-2xl overflow-hidden"
            style={{
              backgroundColor: surface,
              boxShadow: `
                0 0 0 1px rgba(${hexToRgb(text)}, 0.05),
                0 10px 15px -3px rgba(0, 0, 0, 0.1),
                0 20px 40px -10px rgba(0, 0, 0, 0.2),
                0 40px 60px -20px rgba(0, 0, 0, 0.15)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === BORDERED - Simple colored border ===
      case 'bordered':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              border: `3px solid ${primary}`,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === ROUNDED - Extra pill shape ===
      case 'rounded':
        return (
          <div 
            className="relative rounded-3xl overflow-hidden"
            style={{
              backgroundColor: surface,
              boxShadow: `
                0 25px 50px -12px rgba(0, 0, 0, 0.25),
                inset 0 1px 0 rgba(255, 255, 255, 0.1)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === LETTERBOX - Cinematic bars ===
      case 'letterbox':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: '#0a0a0a',
              paddingTop: 12,
              paddingBottom: 12,
              paddingLeft: 8,
              paddingRight: 8,
              boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
            }}
          >
            <div className="relative rounded-lg overflow-hidden" style={{ backgroundColor: surface }}>
              {patternContent}
              {children}
              {overlayContent}
            </div>
          </div>
        );

      // === NOTEBOOK - Paper margin ===
      case 'notebook':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              borderLeft: `4px solid ${primary}`,
              boxShadow: `
                0 4px 6px rgba(0,0,0,0.07),
                0 1px 3px rgba(0,0,0,0.05)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === PIXEL - 8-bit style ===
      case 'pixel':
        return (
          <div className="relative">
            {/* Outer pixel shadow */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundColor: darkenColor(text, 0.2),
                transform: 'translate(6px, 6px)',
              }}
            />
            {/* Main card */}
            <div 
              className="relative overflow-hidden"
              style={{
                backgroundColor: surface,
                border: `4px solid ${text}`,
                borderRadius: 0,
              }}
            >
              {patternContent}
              {children}
              {overlayContent}
            </div>
          </div>
        );

      // === ARCADE - Cabinet style ===
      case 'arcade':
        return (
          <div 
            className="relative rounded-lg overflow-hidden"
            style={{
              backgroundColor: surface,
              border: `4px solid ${primary}`,
              boxShadow: `
                0 0 0 2px #1a1a1a,
                0 0 0 6px ${darkenColor(primary, 0.3)},
                inset 0 0 30px rgba(0,0,0,0.15),
                0 8px 32px rgba(0,0,0,0.4)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === CRT - Retro monitor ===
      case 'crt':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              border: '3px solid #1a1a1a',
              boxShadow: `
                0 0 0 4px #2a2a2a,
                0 0 0 6px #1a1a1a,
                inset 0 0 40px rgba(0,0,0,0.1),
                0 10px 40px rgba(0,0,0,0.4)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
            {/* Screen reflection */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 50%, rgba(0,0,0,0.03) 100%)',
              }}
            />
          </div>
        );

      // === VHS - 80s video ===
      case 'vhs':
        return (
          <div 
            className="relative rounded-lg overflow-hidden"
            style={{
              backgroundColor: surface,
              border: '3px solid #e94560',
              boxShadow: `
                0 0 15px rgba(233, 69, 96, 0.4),
                0 0 30px rgba(233, 69, 96, 0.2),
                0 8px 32px rgba(0,0,0,0.3)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === POLAROID - Instant photo ===
      case 'polaroid':
        return (
          <div 
            className="relative rounded-sm overflow-hidden"
            style={{
              backgroundColor: '#f5f5f0',
              paddingTop: 12,
              paddingBottom: 28,
              paddingLeft: 12,
              paddingRight: 12,
              boxShadow: `
                0 4px 6px rgba(0,0,0,0.1),
                0 10px 20px rgba(0,0,0,0.15),
                0 0 0 1px rgba(0,0,0,0.05)
              `,
            }}
          >
            <div className="relative overflow-hidden rounded-sm" style={{ backgroundColor: surface }}>
              {patternContent}
              {children}
              {overlayContent}
            </div>
          </div>
        );

      // === FILMSTRIP - Perforations ===
      case 'filmstrip':
        return (
          <div 
            className="relative rounded-lg overflow-hidden"
            style={{
              backgroundColor: '#1a1a1a',
              padding: 10,
              boxShadow: `
                0 0 0 2px #333,
                inset 0 0 20px rgba(0,0,0,0.5),
                0 8px 24px rgba(0,0,0,0.4)
              `,
            }}
          >
            <div 
              className="relative rounded overflow-hidden"
              style={{
                backgroundColor: surface,
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
              }}
            >
              {patternContent}
              {children}
              {overlayContent}
            </div>
          </div>
        );

      // === COMIC - Bold outline (border + shadows all from theme "primary" / text so no stray accent) ===
      case 'comic':
        return (
          <div 
            className="relative rounded-lg overflow-hidden"
            style={{
              backgroundColor: surface,
              border: `4px solid ${text}`,
              boxShadow: `
                ${darkenColor(text, 0.25)} 4px 4px 0,
                ${darkenColor(text, 0.45)} 6px 6px 0,
                0 8px 24px rgba(0,0,0,0.2)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === NEON - Glowing border with pulse ===
      case 'neon':
        return (
          <>
            <style>{`
              @keyframes neon-pulse {
                0%, 100% { box-shadow: 0 0 10px ${primary}, 0 0 20px ${primary}aa, 0 0 40px ${primary}55, inset 0 0 20px ${primary}18, 0 8px 32px rgba(0,0,0,0.3); }
                50% { box-shadow: 0 0 14px ${primary}, 0 0 28px ${primary}cc, 0 0 55px ${primary}66, inset 0 0 25px ${primary}22, 0 8px 32px rgba(0,0,0,0.3); }
              }
            `}</style>
            <div
              className="relative rounded-xl overflow-hidden"
              style={{
                backgroundColor: surface,
                border: `2px solid ${primary}`,
                animation: 'neon-pulse 3s ease-in-out infinite',
              }}
            >
              {patternContent}
              {children}
              {overlayContent}
            </div>
          </>
        );

      // === GRADIENT - Rainbow border ===
      case 'gradient':
        return (
          <>
            <style>{`
              @keyframes gradient-rotate {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
              }
            `}</style>
            <div className="relative p-1 rounded-xl" style={{
              background: `linear-gradient(135deg, ${primary}, ${secondary}, ${lightenColor(primary, 0.2)}, ${primary})`,
              backgroundSize: '300% 300%',
              animation: 'gradient-rotate 6s ease-in-out infinite',
            }}>
              <div
                className="relative rounded-lg overflow-hidden"
                style={{
                  backgroundColor: surface,
                }}
              >
                {patternContent}
                {children}
                {overlayContent}
              </div>
            </div>
          </>
        );

      // === GLASS - Frosted effect ===
      case 'glass':
        return (
          <div 
            className="relative rounded-2xl overflow-hidden"
            style={{
              backgroundColor: `rgba(${hexToRgb(surface)}, 0.85)`,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: `
                0 8px 32px rgba(0, 0, 0, 0.15),
                inset 0 1px 0 rgba(255,255,255,0.2)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === MINIMAL - Hairline border ===
      case 'minimal':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              border: `1px solid rgba(${hexToRgb(text)}, 0.1)`,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === OUTLINE - Hollow double stroke ===
      case 'outline':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              boxShadow: `
                0 0 0 2px ${surface},
                0 0 0 4px ${primary},
                0 8px 24px rgba(0,0,0,0.12)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === SOFTGLOW - Diffused halo with gentle pulse ===
      case 'softglow':
        return (
          <>
            <style>{`
              @keyframes softglow-pulse {
                0%, 100% { box-shadow: 0 0 0 1px rgba(${hexToRgb(primary)}, 0.2), 0 0 40px rgba(${hexToRgb(primary)}, 0.12), 0 0 80px rgba(${hexToRgb(primary)}, 0.06), 0 20px 40px -15px rgba(0,0,0,0.2); }
                50% { box-shadow: 0 0 0 1px rgba(${hexToRgb(primary)}, 0.28), 0 0 50px rgba(${hexToRgb(primary)}, 0.18), 0 0 95px rgba(${hexToRgb(primary)}, 0.1), 0 20px 40px -15px rgba(0,0,0,0.2); }
              }
            `}</style>
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{
                backgroundColor: surface,
                animation: 'softglow-pulse 4s ease-in-out infinite',
              }}
            >
              {patternContent}
              {children}
              {overlayContent}
            </div>
          </>
        );

      // === DOUBLE - Inner and outer frame ===
      case 'double':
        return (
          <div 
            className="relative rounded-lg overflow-hidden"
            style={{
              backgroundColor: surface,
              border: `3px solid ${primary}`,
              boxShadow: `
                0 0 0 6px ${secondary},
                0 10px 40px rgba(0, 0, 0, 0.25)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === TRIPLE - Three nested frames ===
      case 'triple':
        return (
          <div 
            className="relative rounded-lg overflow-hidden"
            style={{
              backgroundColor: surface,
              border: `3px solid ${lightenColor(primary, 0.2)}`,
              boxShadow: `
                0 0 0 4px ${primary},
                0 0 0 8px ${darkenColor(primary, 0.2)},
                0 15px 50px rgba(0, 0, 0, 0.3)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === DASHED - Fun dashed border ===
      case 'dashed':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              border: `3px dashed ${primary}`,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === DOTTED - Dot pattern border ===
      case 'dotted':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              border: `4px dotted ${primary}`,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === STICKER - Offset shadow ===
      case 'sticker':
        return (
          <div className="relative">
            <div 
              className="absolute inset-0 rounded-2xl"
              style={{
                backgroundColor: darkenColor(primary, 0.25),
                transform: 'translate(6px, 6px)',
              }}
            />
            <div 
              className="relative rounded-2xl overflow-hidden"
              style={{
                backgroundColor: surface,
                border: `3px solid ${primary}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              {patternContent}
              {children}
              {overlayContent}
            </div>
          </div>
        );

      // === WAVY - Soft rounded edge (large fixed radius so content isn’t cut off) ===
      case 'wavy':
        return (
          <div 
            className="relative overflow-hidden rounded-[1.25rem]"
            style={{
              backgroundColor: surface,
              border: `3px solid ${primary}`,
              boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === GOLD - Metallic gold border ===
      case 'gold':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              border: '3px solid #c9a227',
              boxShadow: `
                0 0 0 1px rgba(201, 162, 39, 0.4),
                inset 0 1px 0 rgba(255, 220, 120, 0.5),
                inset 0 -1px 0 rgba(120, 80, 0, 0.2),
                0 8px 24px rgba(0,0,0,0.2)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === METALLIC - Silver steel ===
      case 'metallic':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              border: '2px solid #a0aec0',
              boxShadow: `
                0 0 0 1px rgba(255,255,255,0.5),
                inset 0 2px 4px rgba(255,255,255,0.4),
                inset 0 -1px 2px rgba(0,0,0,0.1),
                0 8px 24px rgba(0,0,0,0.15)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      // === SPOTLIGHT - Single-edge highlight ===
      case 'spotlight':
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              boxShadow: `
                0 -4px 20px rgba(255,255,255,0.15),
                0 20px 50px -10px rgba(0,0,0,0.35),
                0 4px 15px rgba(0,0,0,0.1)
              `,
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );

      default:
        return (
          <div 
            className="relative rounded-xl overflow-hidden"
            style={{
              backgroundColor: surface,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
          >
            {patternContent}
            {children}
            {overlayContent}
          </div>
        );
    }
  };

  return (
    <Wrapper
      className={`w-full min-w-0 ${fullWidth ? '' : 'max-w-xl'} relative z-10 flex justify-center ${className}`}
      style={{ backfaceVisibility: 'hidden' }}
      {...wrapperProps}
    >
      <div className={fullWidth ? 'w-full min-w-0' : 'min-w-0 w-full max-w-[500px]'}>
        {renderFrame()}
      </div>
    </Wrapper>
  );
};

export default CardFrame;
